import { fromAbsolute, parseDate, toCalendarDate, type CalendarDate } from '@internationalized/date';

import type { CalendarDateRange } from '../../components/DateRangePicker';

export interface IBalanceSnapshot {
  /** ISO instant the snapshot was recorded. */
  timestamp: string;
  balance: number;
}

export interface IDailyValue {
  /** Calendar day in the viewer's zone, `YYYY-MM-DD`. */
  date: string;
  value: number;
}

export interface IValueChange {
  amount: number;
  /** Percent change from the past value (`0.3` is 0.3%); null when the base is zero. */
  percent: number | null;
}

export interface IBalanceHistoryBounds {
  startDate: Date;
  endDate: Date;
  /** The instant just before the range, so a carry-in read never overlaps the ranged rows. */
  carryInDate: Date;
}

/**
 * What a value-over-time chart draws for one account and one date range.
 *
 * - `awaitingFirstValue` — the account has no snapshot at all.
 * - `emptyRange` — the range ends before the account's first snapshot.
 * - `noNewValues` — nothing was recorded in the range; `point` is the carried-in value on the range start.
 * - `oneValue` — a single in-range value on an account with older history.
 * - `trackingStarted` — `point` is the account's first-ever snapshot.
 * - `line` — two or more points, the carry-in included.
 */
export type ValueOverTimeView =
  | { kind: 'awaitingFirstValue' }
  | { kind: 'emptyRange' }
  | { kind: 'noNewValues'; point: IDailyValue }
  | { kind: 'oneValue'; point: IDailyValue }
  | { kind: 'trackingStarted'; point: IDailyValue; reachesToday: boolean }
  | { kind: 'line'; points: IDailyValue[] };

const MIN_VALUE_AXIS_SPAN_RATIO = 0.04;
const VALUE_AXIS_INTERVALS = 4;

function startOfDay(date: CalendarDate, zone: string): Date {
  return date.toDate(zone);
}

/** The inclusive instants a calendar-day range covers in `zone`, plus the carry-in instant before it. */
export function toBalanceHistoryBounds(dateRange: CalendarDateRange, zone: string): IBalanceHistoryBounds {
  const startDate = startOfDay(dateRange.start, zone);
  const endDate = new Date(startOfDay(dateRange.end.add({ days: 1 }), zone).getTime() - 1);

  return { startDate, endDate, carryInDate: new Date(startDate.getTime() - 1) };
}

/**
 * One point per calendar day that has a snapshot — the last snapshot of that day in `zone`.
 * Days with no snapshot are left out rather than filled.
 */
export function toDailySeries(snapshots: readonly IBalanceSnapshot[], zone: string): IDailyValue[] {
  const sorted = [...snapshots].sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));
  const valueByDay = new Map<string, number>();

  for (const snapshot of sorted) {
    const day = toCalendarDate(fromAbsolute(Date.parse(snapshot.timestamp), zone)).toString();
    valueByDay.set(day, snapshot.balance);
  }

  return Array.from(valueByDay, ([date, value]) => ({ date, value })).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * The last instant of the calendar day before `instant`'s day in `zone`.
 *
 * Pass the latest snapshot's timestamp, not the clock: a nightly snapshot is stamped the prior
 * local evening, so a cutoff taken from today would select that same snapshot.
 */
export function previousDayCutoff(instant: Date, zone: string): Date {
  const day = toCalendarDate(fromAbsolute(instant.getTime(), zone));
  return new Date(startOfDay(day, zone).getTime() - 1);
}

/** The first instant of the calendar day `days` before `now`'s day in `zone`. */
export function startOfDayDaysAgo(now: Date, zone: string, days: number): Date {
  const today = toCalendarDate(fromAbsolute(now.getTime(), zone));
  return startOfDay(today.subtract({ days }), zone);
}

export function changeBetween(current: number, past: number): IValueChange {
  const amount = current - past;
  return { amount, percent: past === 0 ? null : (amount / Math.abs(past)) * 100 };
}

export interface ILikeForLikeTotals {
  current: number;
  past: number;
}

/**
 * Both sides summed across only the rows that have a value on both, so a row with no history
 * never reads as growth. Null when no row qualifies.
 */
export function likeForLikeTotals(
  rows: ReadonlyArray<{ current: number | null; past: number | null }>
): ILikeForLikeTotals | null {
  let current = 0;
  let past = 0;
  let qualifying = 0;

  for (const row of rows) {
    if (row.current === null || row.past === null) {
      continue;
    }
    current += row.current;
    past += row.past;
    qualifying += 1;
  }

  return qualifying === 0 ? null : { current, past };
}

/**
 * Picks the chart state for a daily series already limited to `dateRange`.
 *
 * @param args.carryIn The account's latest value strictly before the range, or null when it has none.
 *   It is plotted on the range start unless the series already has a point that day.
 */
export function valueOverTimeView(args: {
  series: readonly IDailyValue[];
  carryIn: number | null;
  dateRange: CalendarDateRange;
  today: CalendarDate;
}): ValueOverTimeView {
  const { series, carryIn, dateRange, today } = args;
  const reachesToday = dateRange.end.compare(today) >= 0;
  const rangeStart = dateRange.start.toString();

  if (carryIn === null) {
    if (series.length === 0) {
      return reachesToday ? { kind: 'awaitingFirstValue' } : { kind: 'emptyRange' };
    }
    if (series.length === 1) {
      return { kind: 'trackingStarted', point: series[0], reachesToday };
    }
    return { kind: 'line', points: [...series] };
  }

  if (series.length === 0) {
    return { kind: 'noNewValues', point: { date: rangeStart, value: carryIn } };
  }

  const points = series[0].date === rangeStart ? [...series] : [{ date: rangeStart, value: carryIn }, ...series];
  return points.length === 1 ? { kind: 'oneValue', point: points[0] } : { kind: 'line', points };
}

/** A calendar day's position on a numeric chart axis: its UTC midnight, so days stay evenly spaced across DST. */
export function dayToChartX(date: string): number {
  const { year, month, day } = parseDate(date);
  return Date.UTC(year, month - 1, day);
}

/** Every calendar day from `first` through `last`, inclusive. */
export function listCalendarDays(first: string, last: string): string[] {
  const end = parseDate(last);
  const days: string[] = [];

  for (let day = parseDate(first); day.compare(end) <= 0; day = day.add({ days: 1 })) {
    days.push(day.toString());
  }

  return days;
}

/** The smallest of 1, 2, 5, 10 × a power of ten that is at least `rawStep`. */
function niceStep(rawStep: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const multiple = [1, 2, 5, 10].find((candidate) => candidate * magnitude >= rawStep) ?? 10;
  return multiple * magnitude;
}

/**
 * Round y-axis ticks fitted to the values rather than anchored at zero; the first and last are the
 * axis domain. A near-flat series is widened to a minimum span, so it neither fills the chart
 * with noise nor steps finer than an abbreviated money label can show.
 */
export function valueAxisTicks(values: readonly number[]): number[] {
  if (values.length === 0) {
    return [];
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const minSpan = Math.max(Math.abs((min + max) / 2) * MIN_VALUE_AXIS_SPAN_RATIO, 1);
  const padding = Math.max((minSpan - (max - min)) / 2, 0);
  const step = niceStep((max - min + 2 * padding) / VALUE_AXIS_INTERVALS);

  const floor = Math.floor((min - padding) / step) * step;
  const first = min >= 0 ? Math.max(0, floor) : floor;
  const count = Math.ceil((max + padding - first) / step - 1e-9);

  return Array.from({ length: count + 1 }, (_, index) => Number((first + index * step).toPrecision(12)));
}
